import { ChevronDown, ChevronRight, ImageOff } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { Dialog } from './dialog'
import { MarkupText } from './markup-text'

/**
 * A turn's email body as the sender formatted it (issue #209).
 *
 * `html` is the API's `html_clean`: already sanitized server-side against a strict allow-list (apps/api
 * app/ingest/turn_html.py). This component still never uses `dangerouslySetInnerHTML`: it parses the HTML with
 * `DOMParser` (which runs nothing) and rebuilds it as React elements from its own allow-list, so a server bug cannot
 * become script here either.
 *
 * - The quoted trail (the first `.pb-quote` / `<blockquote>`, or an "On ... wrote:" / "-----Original Message-----" /
 *   "From: ... Sent:" header) and everything after it is collapsed behind a toggle. Highlights inside it are counted
 *   on the toggle so an inline answer is never hidden silently.
 * - Desk mentions (`zsu[@user:<zuid>]zsu`) and Riley's highlight markers in text render through `MarkupText`.
 * - Images are only the proxy (`/api/v1/attachments/...`) or small embedded rasters; they render as thumbnails that
 *   open full size in a dialog. Blocked images (remote logos, spacers) draw nothing inline: ONE small chip above the
 *   body counts them and says why (#727).
 * - Table layout is kept but drawn without borders and without padding on empty spacer cells; a table draws borders
 *   only when the source set `border` (#727).
 */
export interface EmailHtmlProps {
  html: string
  mentions?: Record<string, string>
  className?: string
  /** Drawn between the new text and the fold (the turn's inline-answer highlights), so it is never folded away. */
  aboveFold?: React.ReactNode
  /** Robot / newsletter mail: a stored `<mark>` is painted layout there, not an answer, so it renders as plain text. */
  automated?: boolean
}

const BLOCK_TAGS = new Set([
  'a',
  'abbr',
  'b',
  'blockquote',
  'br',
  'caption',
  'center',
  'cite',
  'code',
  'col',
  'colgroup',
  'dd',
  'del',
  'div',
  'dl',
  'dt',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'ins',
  'kbd',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  'q',
  's',
  'samp',
  'small',
  'span',
  'strike',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'tt',
  'u',
  'ul',
])
const VOID = new Set(['br', 'hr', 'col'])
const RENAME: Record<string, string> = { center: 'div', strike: 's', tt: 'code' }
const LINK = /^(https?:|mailto:|tel:)/i
const PROXY = /^\/api\/v1\/(?:attachments\/\d{1,20}\/\d{1,20}\/(inline\/\d{1,4}|\d{1,20})|cases\/composer-images\/[0-9a-f-]{36})$/i
const DATA_IMAGE = /^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+$/i
const STYLE_KEYS: Record<string, keyof React.CSSProperties> = {
  'font-weight': 'fontWeight',
  'font-style': 'fontStyle',
  'text-decoration': 'textDecoration',
  'text-decoration-line': 'textDecorationLine',
  'text-align': 'textAlign',
}
const SAFE_STYLE_VALUE = /^[a-z0-9 #%.,-]{1,40}$/i

function safeStyle(raw: string | null): React.CSSProperties | undefined {
  if (!raw) return undefined
  const out: Record<string, string> = {}
  for (const decl of raw.split(';')) {
    const [k, ...rest] = decl.split(':')
    const key = STYLE_KEYS[(k ?? '').trim().toLowerCase()]
    const value = rest.join(':').trim()
    if (key && SAFE_STYLE_VALUE.test(value)) out[key as string] = value
  }
  return Object.keys(out).length ? (out as React.CSSProperties) : undefined
}

/** Elements that start a new line in the text index (so markers can be anchored at a line start). */
const LINE_BREAKING = new Set([
  'BLOCKQUOTE',
  'BR',
  'CENTER',
  'DD',
  'DIV',
  'DL',
  'DT',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'LI',
  'OL',
  'P',
  'PRE',
  'TABLE',
  'TD',
  'TH',
  'TR',
  'UL',
]) // prettier-ignore

type MarkerKind = 'forward' | 'original' | 'earlier' | 'header'

/**
 * Where a quoted or forwarded part starts (#225), as text at the start of a line: Gmail/Apple "On … wrote:", Zoho
 * "---- On … wrote ----", Outlook "-----Original Message-----" and its "From: … / Sent: …" header block, and the
 * forward banners. Markup markers (Gmail `gmail_quote`, Outlook `divRplyFwdMsg`, `<blockquote>`) arrive from the
 * server sanitizer as `.pb-quote`.
 */
const MARKERS: Array<{ kind: MarkerKind; re: string }> = [
  { kind: 'forward', re: '-{2,}\\s*Forwarded message\\s*-{2,}' },
  { kind: 'forward', re: 'Begin forwarded message:' },
  { kind: 'earlier', re: '-{2,}\\s*On\\s[^\\n]{3,300}?\\swrote\\s*-{2,}' },
  { kind: 'original', re: '-{2,}\\s*Original Message\\s*-{2,}' },
  { kind: 'earlier', re: 'On\\s[^\\n]{3,300}?\\swrote:' },
  { kind: 'header', re: 'From:[^\\n]{1,300}\\n\\s*(?:Sent|Date):' },
]
const MARKER_RE = MARKERS.map((m) => ({ kind: m.kind, re: new RegExp(`(?:^|\\n)[ \\t\\u00a0\\u200b]*(${m.re})`, 'gi') }))

interface TextIndex {
  text: string
  nodes: Array<{ node: Text; start: number }>
}

function indexText(root: Node): TextIndex {
  let text = ''
  const nodes: TextIndex['nodes'] = []
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) {
      nodes.push({ node: n as Text, start: text.length })
      text += n.textContent ?? ''
      return
    }
    if (n.nodeType !== Node.ELEMENT_NODE && n.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return
    if (n.nodeType === Node.ELEMENT_NODE && LINE_BREAKING.has((n as Element).tagName)) text += '\n'
    for (const c of Array.from(n.childNodes)) walk(c)
  }
  walk(root)
  return { text, nodes }
}

/** Every marker in the text, in order: [position of the marker itself, kind, end]. */
function findMarkers(text: string): Array<{ at: number; end: number; kind: MarkerKind }> {
  const out: Array<{ at: number; end: number; kind: MarkerKind }> = []
  for (const { kind, re } of MARKER_RE) {
    re.lastIndex = 0
    for (let m = re.exec(text); m; m = re.exec(text)) {
      const body = m[1] ?? ''
      const at = m.index + m[0].length - body.length
      out.push({ at, end: at + body.length, kind })
      if (m[0].length === 0) re.lastIndex++
    }
  }
  return out.sort((x, y) => x.at - y.at)
}

/** The text node where the first text marker starts (split so it begins exactly there), or null. */
function textMarkerStart(body: HTMLElement): Node | null {
  const idx = indexText(body)
  const first = findMarkers(idx.text)[0]
  if (!first) return null
  for (const { node, start } of idx.nodes) {
    const len = node.length
    if (first.at >= start && first.at < start + len) return first.at > start ? node.splitText(first.at - start) : node
  }
  return null
}

function isBlankNode(n: Node): boolean {
  if (n.nodeType === Node.TEXT_NODE) return !(n.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '')
  if (n.nodeType !== Node.ELEMENT_NODE) return true
  const el = n as Element
  if (el.tagName === 'HR' || el.tagName === 'IMG' || el.querySelector('hr, img')) return false
  return !(el.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '')
}

/** Outlook draws an `<hr>` right above "From:": fold from the rule, not from the line under it. */
function ruleAbove(start: Node, body: HTMLElement): Node | null {
  let n: Node | null = start
  for (let steps = 0; n && n !== body && steps < 12; steps++) {
    let prev: Node | null = n.previousSibling
    while (prev && isBlankNode(prev)) prev = prev.previousSibling
    if (prev) {
      if (prev.nodeType === Node.ELEMENT_NODE) {
        const el = prev as Element
        if (el.tagName === 'HR') return el
        const rules = el.querySelectorAll('hr')
        const last = rules[rules.length - 1]
        // A container ending in a rule (nothing meaningful after it) also counts.
        if (last && !(el.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '')) return last
      }
      return null
    }
    n = n.parentNode
  }
  return null
}

/** Where the quoted/forwarded part starts: the earlier of the first `.pb-quote`/`<blockquote>` and a text marker. */
function quoteStart(body: HTMLElement): Node | null {
  const el = body.querySelector('.pb-quote, blockquote')
  const text = textMarkerStart(body)
  let start: Node | null = el
  if (text && (!el || el.compareDocumentPosition(text) & Node.DOCUMENT_POSITION_PRECEDING)) start = text
  if (start && start === text) start = ruleAbove(text, body) ?? text
  return start
}

/** What the fold holds, for its label: earlier messages and forwards (a forward's own "From:" block is not a second). */
export function foldCounts(nodes: Node[]): { earlier: number; forwarded: number } {
  const frag = (nodes[0]?.ownerDocument ?? document).createDocumentFragment()
  for (const n of nodes) frag.append(n.cloneNode(true))
  const markers = findMarkers(indexText(frag).text)
  let earlier = 0
  let forwarded = 0
  let absorbUntil = -1
  for (const m of markers) {
    if (m.kind === 'forward') {
      forwarded++
      absorbUntil = m.end + 200
    } else if (m.kind === 'original') {
      earlier++
      absorbUntil = m.end + 200
    } else if (m.kind === 'header' && m.at <= absorbUntil) {
      absorbUntil = -1
    } else {
      earlier++
      absorbUntil = m.kind === 'earlier' ? -1 : absorbUntil
    }
  }
  if (earlier + forwarded === 0) earlier = 1
  return { earlier, forwarded }
}

export function foldLabel({ earlier, forwarded }: { earlier: number; forwarded: number }): string {
  const parts: string[] = []
  if (earlier) parts.push(`${earlier} earlier ${earlier === 1 ? 'message' : 'messages'}`)
  if (forwarded) parts.push(`${forwarded} forwarded`)
  return `… ${parts.join(' · ')}`
}

/** Split the parsed body at the quoted/forwarded part: [main, folded | null]. A body that is all trail is shown whole. */
export function splitQuoted(body: HTMLElement): [Node[], Node[] | null] {
  const doc = body.ownerDocument
  const start = quoteStart(body)
  if (!start) return [Array.from(body.childNodes), null]
  const range = doc.createRange()
  range.setStartBefore(start)
  range.setEndAfter(body.lastChild as Node)
  const quoted = range.extractContents()
  const main = Array.from(body.childNodes)
  if (!main.some((n) => (n.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '') || (n as Element).querySelector?.('img'))) {
    body.append(quoted)
    return [Array.from(body.childNodes), null]
  }
  return [main, Array.from(quoted.childNodes)]
}

interface Ctx {
  automated?: boolean
  mentions?: Record<string, string>
  onOpenImage: (src: string, alt: string) => void
}

function Thumb({ src, alt, ctx }: { src: string; alt: string; ctx: Ctx }) {
  const [failed, setFailed] = React.useState(false)
  if (failed)
    return (
      <span
        data-slot="email-image-failed"
        className="inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
        title="Patchbay could not load this image from Desk (connect Desk under Me, or open the ticket in Desk)"
      >
        <ImageOff className="size-3.5" aria-hidden />
        {alt ? `Image not loaded: ${alt}` : 'Image not loaded'}
      </span>
    )
  return (
    <button
      type="button"
      data-slot="email-image"
      onClick={() => ctx.onOpenImage(src, alt)}
      className="my-1 inline-block cursor-zoom-in rounded-md border border-border bg-background p-0.5 align-top hover:border-border-strong"
      title={alt ? `${alt} (open full size)` : 'Open full size'}
    >
      <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className="max-h-40 max-w-60 rounded-sm object-contain" />
    </button>
  )
}

function renderNodes(nodes: Node[], ctx: Ctx, keyPrefix: string): React.ReactNode[] {
  return nodes.map((n, i) => renderNode(n, ctx, `${keyPrefix}.${i}`))
}

function renderNode(node: Node, ctx: Ctx, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? ''
    return text ? <MarkupText key={key} text={text} mentions={ctx.mentions} /> : null
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null
  const el = node as Element
  const tag = el.tagName.toLowerCase()
  if (tag === 'img') {
    const src = el.getAttribute('src') ?? ''
    const alt = el.getAttribute('alt') ?? ''
    if (PROXY.test(src) || DATA_IMAGE.test(src)) return <Thumb key={key} src={src} alt={alt} ctx={ctx} />
    return null // counted and explained once by the chip above the body
  }
  if (tag === 'span' && el.classList.contains('pb-img-blocked')) return null
  const children = renderNodes(Array.from(el.childNodes), ctx, key)
  if (tag === 'mark' && ctx.automated) return <React.Fragment key={key}>{children}</React.Fragment>
  if (!BLOCK_TAGS.has(tag)) return <React.Fragment key={key}>{children}</React.Fragment>
  const name = RENAME[tag] ?? tag
  const props: Record<string, unknown> = { key, style: safeStyle(el.getAttribute('style')) }
  if (tag === 'a') {
    const href = (el.getAttribute('href') ?? '').trim()
    if (!LINK.test(href)) return <React.Fragment key={key}>{children}</React.Fragment>
    Object.assign(props, { href, target: '_blank', rel: 'noopener noreferrer nofollow', className: 'text-primary underline' })
  }
  if (tag === 'td' || tag === 'th') {
    const cs = Number(el.getAttribute('colspan'))
    const rs = Number(el.getAttribute('rowspan'))
    if (cs > 1 && cs < 100) props.colSpan = cs
    if (rs > 1 && rs < 100) props.rowSpan = rs
    // Spacer cells (nothing but whitespace) take no padding, so layout tables do not stack empty blocks.
    const shown = el.cloneNode(true) as Element
    for (const blocked of Array.from(shown.querySelectorAll('.pb-img-blocked'))) blocked.remove()
    const empty = !(shown.textContent ?? '').replace(/[\s\u00a0\u200b]/g, '') && !shown.querySelector('img, table, hr')
    props.className = empty ? undefined : 'px-2 py-1 align-top'
  }
  if (tag === 'ol') {
    const start = Number(el.getAttribute('start'))
    if (start > 0 && start < 10000) props.start = start
  }
  if (tag === 'table') {
    const bordered = Number(el.getAttribute('border')) > 0
    props.className = cn(
      'my-2 border-collapse text-sm',
      bordered && 'border border-border [&_td]:border [&_td]:border-border [&_th]:border [&_th]:border-border',
    )
  }
  if (tag === 'mark') props.className = 'rounded-xs bg-warning-soft px-0.5 text-foreground'
  if (tag === 'blockquote') props.className = 'border-l-2 border-border pl-3 text-muted-foreground'
  if (VOID.has(tag)) return React.createElement(name, props)
  return React.createElement(name, props, ...children)
}

function BlockedImages({ count, reason }: { count: number; reason: string }) {
  return (
    <span
      data-slot="email-image-blocked"
      title={reason}
      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
    >
      <ImageOff className="size-3.5" aria-hidden />
      <span>{count === 1 ? '1 image not loaded' : `${count} images not loaded`}</span>
    </span>
  )
}

/** Images the sanitizer or this renderer blocked, and the first reason given. */
function blockedImages(nodes: Node[]): { count: number; reason: string } {
  let count = 0
  let reason = ''
  for (const node of nodes) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue
    const el = node as Element
    const hits = [el, ...Array.from(el.querySelectorAll('span.pb-img-blocked, img'))].filter((e) => {
      if (e.tagName === 'IMG') {
        const src = e.getAttribute('src') ?? ''
        return !(PROXY.test(src) || DATA_IMAGE.test(src))
      }
      return e.classList.contains('pb-img-blocked')
    })
    count += hits.length
    if (!reason && hits[0]) reason = hits[0].getAttribute('data-reason') ?? 'Image not shown'
  }
  return { count, reason }
}

function countMarks(nodes: Node[]): number {
  let n = 0
  for (const node of nodes) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue
    const el = node as Element
    n += (el.tagName === 'MARK' ? 1 : 0) + el.querySelectorAll('mark').length
  }
  return n
}

export function EmailHtml({ html, mentions, className, aboveFold, automated }: EmailHtmlProps) {
  const [image, setImage] = React.useState<{ src: string; alt: string } | null>(null)
  const [showQuoted, setShowQuoted] = React.useState(false)
  const parsed = React.useMemo(() => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const [main, quoted] = splitQuoted(doc.body)
    const blocked = blockedImages([...main, ...(quoted ?? [])])
    return {
      main,
      quoted,
      blocked,
      marks: quoted && !automated ? countMarks(quoted) : 0,
      label: quoted ? foldLabel(foldCounts(quoted)) : '',
    }
  }, [html, automated])
  const ctx: Ctx = { automated, mentions, onOpenImage: (src, alt) => setImage({ src, alt }) }
  return (
    <div data-slot="email-html" className={cn('email-html space-y-2 break-words text-base text-foreground/90', className)}>
      {parsed.blocked.count ? <BlockedImages count={parsed.blocked.count} reason={parsed.blocked.reason} /> : null}
      <div className="email-prose" data-slot="email-prose">
        {renderNodes(parsed.main, ctx, 'm')}
      </div>
      {aboveFold}
      {parsed.quoted ? (
        <div>
          <button
            type="button"
            aria-expanded={showQuoted}
            data-slot="email-fold"
            aria-label={`${showQuoted ? 'Hide' : 'Show'} quoted text: ${parsed.label.replace(/^… /, '')}${
              parsed.marks ? `, ${parsed.marks} highlighted ${parsed.marks === 1 ? 'answer' : 'answers'} inside` : ''
            }`}
            onClick={() => setShowQuoted((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted"
          >
            {showQuoted ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
            {showQuoted ? 'Hide quoted text' : parsed.label}
            {parsed.marks ? (
              <span className="text-warning">
                {' '}
                · {parsed.marks} highlighted {parsed.marks === 1 ? 'answer' : 'answers'} inside
              </span>
            ) : null}
          </button>
          {showQuoted ? (
            <div data-slot="email-quoted" className="email-prose mt-1 border-l-2 border-border pl-3 text-sm text-muted-foreground">
              {renderNodes(parsed.quoted, ctx, 'q')}
            </div>
          ) : null}
        </div>
      ) : null}
      <Dialog
        open={image !== null}
        onOpenChange={(open) => !open && setImage(null)}
        title={image?.alt || 'Image'}
        className="w-[min(1200px,calc(100vw-2rem))]"
      >
        {image ? (
          <div className="space-y-2">
            <img src={image.src} alt={image.alt} className="mx-auto max-h-[70vh] max-w-full object-contain" />
            {image.src.startsWith('/') ? (
              <a href={image.src} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline">
                Open in a new tab
              </a>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </div>
  )
}
